import { Option } from '@bbl-digital/snorre-v2'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ArchieveFileCategory } from 'shared/microServices/portalDocuments/models/ArchieveFileCategory'
import { BoardDocumentFilterSharedWithBblEnum } from 'shared/microServices/portalDocuments/models/BoardDocumentQueryModel'
import { BoardDocumentResponse } from 'shared/microServices/portalDocuments/models/BoardDocumentResponse'
import { emptySplitApi, RootState } from 'shared/store/store'

type Filter = {
  searchString?: string
  documentType?: string
  fileCategory?: ArchieveFileCategory
  currentPage?: number
  pageSize?: number
  currentPageList?: number
  pageSizeList?: number
  startDate?: string
  endDate?: string
  sharedWithBbl?: BoardDocumentFilterSharedWithBblEnum
  isBoardTabActive?: boolean
  isOlderThan5Years?: boolean
  selectedUnits?: Option[]
  selectedTags?: Option[]
}

const initialState: Filter = {
  searchString: '',
  selectedTags: [],
  documentType: '',
  fileCategory: undefined,
  currentPage: 0,
  pageSize: 10,
  currentPageList: 0,
  pageSizeList: 50,
  startDate: '',
  endDate: '',
  sharedWithBbl: BoardDocumentFilterSharedWithBblEnum.Unfiltered,
  isOlderThan5Years: false,
  isBoardTabActive: true,
  selectedUnits: [],
}

type RemoveLabelAndUnitParams = {
  fileId: string
  labelId?: string
  unitId?: string
}

const removeLabelAndUnitTagsThunk = createAsyncThunk(
  'boardArchive/removeLabelAndUnitTags',
  async (
    { fileId, labelId, unitId }: RemoveLabelAndUnitParams,
    { dispatch, getState }
  ) => {
    const state = getState() as RootState
    const apiState = state.api

    // Update all cached entries for queryBoardDocuments
    if (apiState?.queries) {
      Object.entries(apiState.queries).forEach(([queryKey, queryState]) => {
        if (queryKey.startsWith('queryBoardDocuments(') && queryState?.data) {
          const queryArgMatch = queryKey.match(/queryBoardDocuments\((.*)\)/)
          if (queryArgMatch) {
            const queryArg = JSON.parse(queryArgMatch[1])
            // Use type assertion to bypass TypeScript checking for dynamic endpoint names
            const updateAction = (emptySplitApi.util.updateQueryData as any)(
              'queryBoardDocuments',
              queryArg,
              (draft: BoardDocumentResponse | undefined) => {
                if (!draft) return
                const document = draft.documents.find(
                  (doc) => doc.id === fileId
                )
                if (document) {
                  if (labelId) {
                    document.labels = document.labels.filter(
                      (label) => label.id !== labelId
                    )
                  }
                  if (unitId) {
                    document.units =
                      document.units?.filter((unit) => unit.id !== unitId) || []
                  }
                }
              }
            )
            dispatch(updateAction)
          }
        }
      })
    }
  }
)

const boardArchiveSlice = createSlice({
  name: 'boardArchive',
  initialState,
  reducers: {
    resetBoardArchiveFilters: (state) => {
      state.searchString = initialState.searchString
      state.selectedTags = initialState.selectedTags
      state.sharedWithBbl = initialState.sharedWithBbl
      state.documentType = initialState.documentType
      state.fileCategory = initialState.fileCategory
      state.isOlderThan5Years = initialState.isOlderThan5Years
      state.startDate = initialState.startDate
      state.endDate = initialState.endDate
      state.currentPage = initialState.currentPage
      state.pageSize = initialState.pageSize
      state.currentPageList = initialState.currentPageList
      state.pageSizeList = initialState.pageSizeList
      state.isOlderThan5Years = initialState.isOlderThan5Years
      state.selectedUnits = initialState.selectedUnits
    },
    setBoardSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload
    },
    setBoardSelectedTags: (state, action: PayloadAction<Option[]>) => {
      state.selectedTags = action.payload
    },
    setBoardSharedWithBbl: (
      state,
      action: PayloadAction<BoardDocumentFilterSharedWithBblEnum>
    ) => {
      state.sharedWithBbl = action.payload
    },
    setBoardDocumentType: (state, action: PayloadAction<string>) => {
      state.documentType = action.payload
    },
    setBoardFileCategory: (
      state,
      action: PayloadAction<ArchieveFileCategory>
    ) => {
      state.fileCategory = action.payload
    },
    setBoardIsOlderThan5Years: (state, action: PayloadAction<boolean>) => {
      state.isOlderThan5Years = action.payload
    },
    setBoardCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload
    },
    setBoardPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
    },
    setBoardStartDate: (state, action: PayloadAction<string>) => {
      state.startDate = action.payload
    },
    setBoardEndDate: (state, action: PayloadAction<string>) => {
      state.endDate = action.payload
    },
    setBoardFilter: (state, action: PayloadAction<Filter>) => {
      state.searchString = action.payload.searchString
      state.selectedTags = action.payload.selectedTags
      state.sharedWithBbl = action.payload.sharedWithBbl
      state.documentType = action.payload.documentType
      state.fileCategory = action.payload.fileCategory
      state.currentPage = action.payload.currentPage
      state.pageSize = action.payload.pageSize
      state.currentPageList = action.payload.currentPageList
      state.pageSizeList = action.payload.pageSizeList
      state.startDate = action.payload.startDate
      state.endDate = action.payload.endDate
      state.isOlderThan5Years = action.payload.isOlderThan5Years
      state.selectedUnits = action.payload.selectedUnits
    },
    setBoardIsBoardTabActive: (state, action: PayloadAction<boolean>) => {
      state.isBoardTabActive = action.payload
    },
    setBoardCurrentPageList: (state, action: PayloadAction<number>) => {
      state.currentPageList = action.payload
    },
    setBoardPageSizeList: (state, action: PayloadAction<number>) => {
      state.pageSizeList = action.payload
    },
    setBoardSelectedUnits: (state, action: PayloadAction<Option[]>) => {
      state.selectedUnits = action.payload
    },
  },
})

export const {
  resetBoardArchiveFilters,
  setBoardSearchString,
  setBoardSelectedTags,
  setBoardSharedWithBbl,
  setBoardDocumentType,
  setBoardFileCategory,
  setBoardCurrentPage,
  setBoardPageSize,
  setBoardFilter,
  setBoardIsBoardTabActive,
  setBoardStartDate,
  setBoardEndDate,
  setBoardIsOlderThan5Years,
  setBoardCurrentPageList,
  setBoardPageSizeList,
  setBoardSelectedUnits,
} = boardArchiveSlice.actions

export default boardArchiveSlice

export const getBoardArchiveFilters = (state: RootState) => state.boardArchive

export const getBoardArchiveIsBoardTabActive = (state: RootState) =>
  state.boardArchive.isBoardTabActive

export const getBoardArchiveCurrentPage = (state: RootState) =>
  state.boardArchive.currentPage

export const getBoardArchivePageSize = (state: RootState) =>
  state.boardArchive.pageSize

export const getBoardArchiveCurrentPageList = (state: RootState) =>
  state.boardArchive.currentPageList

export const getBoardArchivePageSizeList = (state: RootState) =>
  state.boardArchive.pageSizeList

export { removeLabelAndUnitTagsThunk }
